import React, { useState, useEffect } from 'react';
import {
  collection, query, where, orderBy, onSnapshot,
  doc, updateDoc, getDoc, setDoc, serverTimestamp
} from 'firebase/firestore';
import { firestore } from '../lib/firebaseConfig';
import { useAuth } from '../context/AuthContext';
import PageWrapper from '../components/PageWrapper';
import PageTitle from '../components/PageTitle';
import Card from '../components/Card';
import Button from '../components/Button';

const FIELD_LABELS = {
  paymentDueDate: 'Termin platnosci',
  payment_status: 'Status',
  paidAmount: 'Zaplacono',
  remainingAmount: 'Pozostalo',
  paymentDates: 'Data zaplaty',
  withdrawnAmount: 'Wyplacono',
  dateField: 'Wyplacono dnia',
  dueType: 'Typ terminu',
  linkedStage: 'Powiazany etap',
  fixedDueDate: 'Staly termin',
};

const STATUS_COLORS = {
  pending: 'bg-amber-100 text-amber-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
};

const STATUS_LABELS = {
  pending: 'Oczekuje',
  approved: 'Zatwierdzono',
  rejected: 'Odrzucono',
};

const ChangeRequests = () => {
  const { currentUser } = useAuth();
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [processing, setProcessing] = useState(null);

  useEffect(() => {
    const q = query(
      collection(firestore, 'changeRequests'),
      where('status', '==', filter),
      orderBy('createdAt', 'desc')
    );

    const unsub = onSnapshot(q, (snap) => {
      setRequests(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    return () => unsub();
  }, [filter]);

  const handleApprove = async (req) => {
    setProcessing(req.id);
    try {
      // Apply changes to the payment document
      const payRef = doc(
        firestore,
        `investments/${req.investmentId}/flats/${req.flatId}/payments/${req.paymentId}`
      );

      const updates = {};
      for (const [field, change] of Object.entries(req.changes)) {
        updates[field] = change.to;
      }

      await updateDoc(payRef, updates);

      // Mark request as approved
      await updateDoc(doc(firestore, 'changeRequests', req.id), {
        status: 'approved',
        resolvedAt: serverTimestamp(),
        resolvedBy: currentUser?.uid || '',
      });
    } catch (error) {
      console.error('Error approving change request:', error);
      alert('Blad podczas zatwierdzania zmiany.');
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (req) => {
    const reason = prompt('Podaj powod odrzucenia (opcjonalnie):');
    if (reason === null) return; // cancelled

    setProcessing(req.id);
    try {
      await updateDoc(doc(firestore, 'changeRequests', req.id), {
        status: 'rejected',
        rejectionReason: reason || '',
        resolvedAt: serverTimestamp(),
        resolvedBy: currentUser?.uid || '',
      });
    } catch (error) {
      console.error('Error rejecting change request:', error);
      alert('Blad podczas odrzucania zmiany.');
    } finally {
      setProcessing(null);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const d = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return d.toLocaleDateString('pl-PL') + ' ' + d.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <PageWrapper>
      <PageTitle>Wnioski o zmiane</PageTitle>

      {/* Filters */}
      <div className="flex gap-2 mt-4 mb-6">
        {['pending', 'approved', 'rejected'].map((status) => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition ${
              filter === status
                ? 'bg-[var(--maroon)] text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {STATUS_LABELS[status]}
            {status === 'pending' && requests.length > 0 && filter === 'pending'
              ? ` (${requests.length})`
              : ''}
          </button>
        ))}
      </div>

      {requests.length === 0 ? (
        <Card className="p-6 text-center text-gray-500">
          Brak wnioskow {STATUS_LABELS[filter].toLowerCase()}.
        </Card>
      ) : (
        <div className="space-y-4">
          {requests.map((req) => (
            <Card key={req.id} className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-[var(--electric-blue)]">
                    Lokal {req.flatCode || req.flatId} &mdash; Rata {req.paymentNumber}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Zgloszono: {formatDate(req.createdAt)} przez {req.requestedByEmail}
                  </p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[req.status]}`}>
                  {STATUS_LABELS[req.status]}
                </span>
              </div>

              {/* Changes table */}
              <div className="border rounded-md overflow-hidden mb-4">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-gray-600 font-medium">Pole</th>
                      <th className="px-4 py-2 text-left text-gray-600 font-medium">Bylo</th>
                      <th className="px-4 py-2 text-left text-gray-600 font-medium">Proponowane</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(req.changes || {}).map(([field, change]) => (
                      <tr key={field} className="border-t">
                        <td className="px-4 py-2 font-medium text-[var(--maroon)]">
                          {FIELD_LABELS[field] || field}
                        </td>
                        <td className="px-4 py-2 text-gray-500">
                          {change.from || '(brak)'}
                        </td>
                        <td className="px-4 py-2 font-semibold text-[var(--electric-blue)]">
                          {change.to || '(brak)'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Note from PM */}
              {req.note && (
                <div className="bg-blue-50 border border-blue-200 rounded-md px-4 py-3 mb-4 text-sm text-blue-800">
                  <strong>Uzasadnienie:</strong> {req.note}
                </div>
              )}

              {/* Rejection reason */}
              {req.status === 'rejected' && req.rejectionReason && (
                <div className="bg-red-50 border border-red-200 rounded-md px-4 py-3 mb-4 text-sm text-red-800">
                  <strong>Powod odrzucenia:</strong> {req.rejectionReason}
                </div>
              )}

              {/* Action buttons */}
              {req.status === 'pending' && (
                <div className="flex gap-3 mt-4">
                  <Button
                    variant="default"
                    onClick={() => handleApprove(req)}
                    disabled={processing === req.id}
                  >
                    {processing === req.id ? 'Przetwarzanie...' : 'Zatwierdz'}
                  </Button>
                  <Button
                    variant="bordered"
                    onClick={() => handleReject(req)}
                    disabled={processing === req.id}
                    className="text-red-600 border-red-300 hover:bg-red-50"
                  >
                    Odrzuc
                  </Button>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </PageWrapper>
  );
};

export default ChangeRequests;
